import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Lock, Workflow } from "lucide-react";
import { cn } from "../../utils/cn";

// ─── Notas privadas en el hilo ───────────────────────────────────────────────
// Una nota privada es un mensaje `sender_role: 'system'` con
// `metadata.kind === 'private_note'`: la ve solo el equipo (este hilo se monta
// únicamente en la bandeja de agentes), nunca el contacto ni la IA. Se pinta
// como una burbuja ámbar del lado del equipo, misma familia visual que la
// "Nota interna" de la IA (Badge warning + candado), para que se lean como
// hermanas.

export function isPrivateNote(msg) {
  return msg?.sender_role === "system" && msg?.metadata?.kind === "private_note";
}

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Nombres mencionados en la nota, resueltos con getDisplayName. Un id que no
 * resuelve (getDisplayName devuelve el id crudo) se omite: no hay "@Nombre"
 * que buscar en el texto.
 * @returns {Array<{id:string, name:string}>}
 */
export function mentionedNames(msg, getDisplayName) {
  const ids = Array.isArray(msg?.metadata?.mentions) ? msg.metadata.mentions : [];
  const out = [];
  for (const id of ids) {
    if (typeof id !== "string" || !id) continue;
    const name = getDisplayName ? getDisplayName(id) : "";
    if (name && name !== id) out.push({ id, name });
  }
  return out;
}

/**
 * Parte el contenido en segmentos de texto y de mención. Exportada para test:
 * es la única lógica de la burbuja.
 * @returns {Array<{text:string, mentionId?:string}>}
 */
export function splitMentions(content, mentions) {
  const text = String(content || "");
  if (!text || !mentions?.length) return [{ text }];
  // Más largos primero: "@Ana María" debe ganarle a "@Ana".
  const sorted = [...mentions].sort((a, b) => b.name.length - a.name.length);
  const byName = new Map(sorted.map((m) => [m.name.toLowerCase(), m.id]));
  const re = new RegExp(`@(${sorted.map((m) => escapeRegExp(m.name)).join("|")})`, "gi");
  const parts = [];
  let last = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push({ text: text.slice(last, match.index) });
    parts.push({ text: match[0], mentionId: byName.get(match[1].toLowerCase()) });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}

/** Autor visible de la nota (o null si es de una regla de automatización). */
export function noteAuthorLabel(msg, getDisplayName, currentUserId, t) {
  const meta = msg?.metadata || {};
  if (meta.rule_name) return null;
  const authorId = meta.author_id || msg?.sender_id || null;
  if (authorId && currentUserId && authorId === currentUserId) return t("Tú");
  const resolved = authorId && getDisplayName ? getDisplayName(authorId) : "";
  return (
    meta.author_name ||
    msg?.sender_name ||
    (resolved && resolved !== authorId ? resolved : null) ||
    t("Equipo")
  );
}

// Rótulo "Automatización: <regla>" para lo que hizo una regla (asignación,
// nota). Second visual pass: quiet muted text with the workflow icon instead of
// a blue badge, so automation events stop competing with real messages.
export function AutomationTag({ ruleName }) {
  const { t } = useTranslation();
  if (!ruleName) return null;
  return (
    <span className="inline-flex items-center gap-[4px] max-w-full text-[11px] leading-[16px] font-medium text-fg-muted">
      <Workflow className="w-3 h-3 shrink-0" aria-hidden="true" />
      <span className="truncate">
        {t("Automatización: {{regla}}").replace("{{regla}}", ruleName)}
      </span>
    </span>
  );
}

export function PrivateNoteBubble({ msg, getDisplayName, currentUserId }) {
  const { t } = useTranslation();
  const meta = msg.metadata || {};
  const ts = msg.created_at || msg.createdAt || null;
  const timeStr = ts ? dayjs(ts).format("HH:mm") : "";
  const author = noteAuthorLabel(msg, getDisplayName, currentUserId, t);
  const parts = splitMentions(msg.content, mentionedNames(msg, getDisplayName));

  return (
    // Same geometry as an agent row (bubble + 28px avatar slot, gap-2) so the
    // note lines up with the agent's own bubbles. Second visual pass: the
    // "Nota privada" badge, the author and the lock live INSIDE the note as
    // one header line (before: a pill above + a lock avatar beside = three
    // labels for one event). Dark mode: a faint amber wash (warning at 10%)
    // with an amber outline instead of --status-warning-soft, which in dark is
    // the same brown as the agent bubble (--brand-900); bg-subtle alone would
    // match the AI reply.
    <div className="mt-[16px] flex items-end justify-end gap-2">
      <div className="max-w-[85%] sm:max-w-[70%] flex flex-col items-end">
        {meta.rule_name && (
          <div className="mb-[4px] px-[4px]">
            <AutomationTag ruleName={meta.rule_name} />
          </div>
        )}
        <div className="rounded-[16px] rounded-br-[6px] px-[12px] py-[8px] text-[14px] leading-[20px] break-words whitespace-pre-wrap bg-status-warning-soft text-fg border border-status-warning/20 dark:bg-status-warning/10 dark:border-status-warning/40">
          <div className="mb-[2px] flex items-center gap-[6px] text-[11px] leading-[16px] whitespace-normal">
            <span className="inline-flex items-center gap-[4px] font-semibold text-status-warning">
              <Lock className="w-3 h-3 shrink-0" aria-hidden="true" />
              {t("Nota privada")}
            </span>
            {author && (
              <>
                <span className="text-fg-muted" aria-hidden="true">·</span>
                <span className="font-medium text-fg-muted">{author}</span>
              </>
            )}
          </div>
          {parts.map((part, i) =>
            part.mentionId ? (
              <span
                key={i}
                className={cn(
                  "font-semibold text-status-warning",
                  part.mentionId === currentUserId && "rounded px-0.5 bg-status-warning/15",
                )}
              >
                {part.text}
              </span>
            ) : (
              <React.Fragment key={i}>{part.text}</React.Fragment>
            ),
          )}
          {timeStr && (
            <div className="text-[11px] mt-[2px] flex justify-end text-fg-muted select-none">
              {timeStr}
            </div>
          )}
        </div>
      </div>
      <span className="w-7 shrink-0" aria-hidden="true" />
    </div>
  );
}
